import { NgClass } from '@angular/common';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  QueryList,
  signal,
  ViewChildren,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { AbstractDynamicComponent } from '@components/abstracts';
import { CartesDynamiques } from '@components/cartes-dynamiques/models';
import { TranslatePipe } from '@ngx-translate/core';
import { ButtonComponent } from '@shared/components/button';
import { ImageComponent } from '@shared/components/image';
import { TitleComponent } from '@shared/components/title';
import { LogsService } from '@shared/services/logs';

const WHITE_OVERLAY_OPACITY = 0.7;

@Component({
  selector: 'app-cartes-dynamiques',
  imports: [TitleComponent, ButtonComponent, NgClass, ImageComponent, TranslatePipe, RouterLink],
  templateUrl: './cartes-dynamiques.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CartesDynamiquesComponent extends AbstractDynamicComponent<CartesDynamiques> {
  @ViewChildren('cardItem') cardItems!: QueryList<ElementRef<HTMLElement>>;
  protected readonly overlayOpacities = signal<number[]>([]);
  private readonly logs = inject(LogsService);

  constructor() {
    super();
    afterNextRender(() => {
      this.initializeOpacities();
      this.setupIntersectionObserver();
    });
  }

  private initializeOpacities(): void {
    const itemsCount = this.data.items?.length || 0;
    this.overlayOpacities.set(Array<number>(itemsCount).fill(0));
  }

  private setupIntersectionObserver(): void {
    const cards = this.cardItems.toArray();

    if (cards.length === 0) {
      this.logs.warn('CartesDynamiquesComponent', 'No card found');
      return;
    }

    const options: IntersectionObserverInit = {
      threshold: Array.from({ length: 21 }, (_, i) => i / 20),
      root: null,
      rootMargin: '0px',
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const currentCard = entry.target as HTMLElement;
        const currentIndex = cards.findIndex((item) => item.nativeElement === currentCard);

        if (currentIndex > 0 && entry.isIntersecting) {
          const overlayOpacity = Math.min(WHITE_OVERLAY_OPACITY, entry.intersectionRatio * WHITE_OVERLAY_OPACITY);

          const opacities = [...this.overlayOpacities()];
          opacities[currentIndex - 1] = overlayOpacity;
          this.overlayOpacities.set(opacities);
        }
      });
    }, options);

    cards.forEach((cardItem, index) => {
      if (index > 0) {
        observer.observe(cardItem.nativeElement);
      }
    });
  }
}
